import React, { useState } from 'react';
import { AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const LEVELS = ['overview', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5'];
const LEVEL_LABELS = {
  overview: 'OV',
  level_1: 'L1',
  level_2: 'L2',
  level_3: 'L3',
  level_4: 'L4',
  level_5: 'L5',
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronsUpDown className="w-3 h-3 text-purple-400" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-purple-200" />
    : <ChevronDown className="w-3 h-3 text-purple-200" />;
};

const CoverageGapsTable = ({ data, loading }) => {
  const [sortField, setSortField] = useState('coverageCount');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="h-5 bg-white/20 rounded animate-pulse w-48 mb-6" />
        {[1, 2, 3, 4, 5]?.map(i => (
          <div key={i} className="h-12 bg-white/10 rounded-xl animate-pulse mb-2" />
        ))}
      </div>
    );
  }

  let filtered = (data || [])?.filter(row => {
    const matchSearch = !search || row?.songTitle?.toLowerCase()?.includes(search?.toLowerCase());
    const matchLevel = filterLevel === 'all' || row?.missingLevels?.includes(filterLevel);
    return matchSearch && matchLevel;
  });

  filtered = [...filtered]?.sort((a, b) => {
    const av = a?.[sortField] ?? 0;
    const bv = b?.[sortField] ?? 0;
    if (typeof av === 'string') return sortDir === 'asc' ? av?.localeCompare(bv) : bv?.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Coverage Gaps</h2>
            <p className="text-purple-300 text-xs mt-0.5">
              {filtered?.length} song{filtered?.length !== 1 ? 's' : ''} missing at least one level video
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Search songs..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-1.5 text-white text-sm placeholder-purple-400 focus:outline-none focus:border-purple-400 w-40"
          />
          <select
            value={filterLevel}
            onChange={e => setFilterLevel(e?.target?.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-1.5 text-white text-sm focus:outline-none focus:border-purple-400"
          >
            <option value="all" className="bg-gray-900">All Levels</option>
            {LEVELS?.map(lvl => (
              <option key={lvl} value={lvl} className="bg-gray-900">
                Missing {lvl === 'overview' ? 'Overview' : lvl?.replace('_', ' ')?.replace('level', 'Level')}
              </option>
            ))}
          </select>
        </div>
      </div>
      {filtered?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-purple-300">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-3">
            <span className="text-emerald-400 text-xl">✓</span>
          </div>
          <p className="text-lg font-medium text-emerald-300">
            {search || filterLevel !== 'all' ? 'No matching gaps found' : 'Full coverage!'}
          </p>
          <p className="text-sm mt-1">
            {search || filterLevel !== 'all' ? 'Try adjusting your filters' : 'All songs have videos for every level'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-white/10">
                <th
                  className="text-left py-3 px-3 text-purple-300 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('songTitle')}
                >
                  <div className="flex items-center gap-1">
                    Song <SortIcon field="songTitle" sortField={sortField} sortDir={sortDir} />
                  </div>
                </th>
                <th
                  className="text-center py-3 px-3 text-purple-300 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('coverageCount')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Coverage <SortIcon field="coverageCount" sortField={sortField} sortDir={sortDir} />
                  </div>
                </th>
                {LEVELS?.map(lvl => (
                  <th key={lvl} className="text-center py-3 px-2 text-purple-300 text-xs font-semibold uppercase tracking-wide">
                    {LEVEL_LABELS?.[lvl]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered?.map((row, i) => (
                <tr key={row?.songId || i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-3">
                    <p className="text-white text-sm font-medium truncate max-w-[200px]">
                      {row?.songTitle}
                    </p>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-xs font-bold ${
                        row?.coveragePct >= 80 ? 'text-emerald-300' :
                        row?.coveragePct >= 50 ? 'text-amber-300' : 'text-red-300'
                      }`}>
                        {row?.coverageCount}/6
                      </span>
                      <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            row?.coveragePct >= 80 ? 'bg-emerald-400' :
                            row?.coveragePct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                          }`}
                          style={{ width: `${row?.coveragePct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  {LEVELS?.map(lvl => {
                    const status = row?.levelStatus?.[lvl];
                    const isMissing = !status;
                    return (
                      <td key={lvl} className="py-3 px-2 text-center">
                        {isMissing ? (
                          <div className="w-6 h-6 mx-auto rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center" title="Missing">
                            <span className="text-red-400 text-xs font-bold">✕</span>
                          </div>
                        ) : (
                          <div
                            className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center ${
                              status?.published ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-amber-500/20 border border-amber-500/40'
                            }`}
                            title={status?.published ? 'Published' : `Status: ${status?.status}`}
                          >
                            <span className={`text-xs font-bold ${status?.published ? 'text-emerald-400' : 'text-amber-400'}`}>✓</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
            <span className="text-emerald-400 text-xs">✓</span>
          </div>
          <span className="text-purple-300 text-xs">Published</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <span className="text-amber-400 text-xs">✓</span>
          </div>
          <span className="text-purple-300 text-xs">Uploaded (not published)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
            <span className="text-red-400 text-xs">✕</span>
          </div>
          <span className="text-purple-300 text-xs">Missing</span>
        </div>
      </div>
    </div>
  );
};

export default CoverageGapsTable;
